import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { JobsService } from '../../services/jobs.service';
import { JobDetailDto } from '@models/job-detail-dto.interface';
import { EditJobComponent, EditJobDialogData } from './edit-job/edit-job.component';
import { JobTestsComponent } from './tests/job-tests.component';
import { JobAttachmentsComponent } from './attachments/job-attachments.component';
import { JobWorksheetComponent } from './worksheet/job-worksheet.component';
import { ServiceCompanyComponent } from './service-company/service-company.component';
import { MaterialsQaqcComponent } from './materials-qaqc/materials-qaqc.component';
import { CommentsComponent } from '../../shared/components/comments/comments.component';
import { JobNotesProvider } from '../../services/job-notes-provider.service';

@Component({
  selector: 'app-job-details',
  standalone: true,
  imports: [CommonModule, DatePipe, RouterLink, MatProgressSpinnerModule, JobTestsComponent, JobAttachmentsComponent, JobWorksheetComponent, ServiceCompanyComponent, MaterialsQaqcComponent, MatTabsModule, CommentsComponent, MatButtonModule, MatIconModule],
  templateUrl: './job-details.component.html',
  styleUrl: './job-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly jobsService = inject(JobsService);
  private readonly dialog = inject(MatDialog);
  protected readonly jobNotesProvider = inject(JobNotesProvider);

  protected job = signal<JobDetailDto | null>(null);
  protected isLoading = signal(false);
  protected projectName = signal<string | null>(null);
  protected dashboardNodeType = signal<number | null>(null);
  protected dashboardProjectId = signal<number | null>(null);
  protected dashboardYear = signal<number | null>(null);

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(queryParams => {
      this.projectName.set(queryParams.get('projectName'));
      this.dashboardNodeType.set(this.toNumberOrNull(queryParams.get('nodeType')));
      this.dashboardProjectId.set(this.toNumberOrNull(queryParams.get('projectId')));
      this.dashboardYear.set(this.toNumberOrNull(queryParams.get('year')));
    });
    this.route.paramMap.subscribe(params => {
      const id = Number(params.get('id'));      
      if (id) {
        this.loadJob(id);
      }
    });
  }

  protected getJobsDashboardQueryParams(jobData: JobDetailDto): { nodeType: number; projectId: number; projectName: string; year: number } {
    return {
      nodeType: this.dashboardNodeType() ?? 1,
      projectId: this.dashboardProjectId() ?? jobData.projectId,
      projectName: this.projectName() ?? '',
      year: this.dashboardYear() ?? this.getYearFromDate(jobData.initiatedOn)
    };
  }

  protected getJobTypeName(jobType: number | null | undefined): string {
    switch (jobType) {
      case 2:
        return 'Completion';
      case 3:
        return 'Cement';
      case 99:
        return 'Other';
      case 1:
      default:
        return 'Drilling';
    }
  }


  private toNumberOrNull(value: string | null): number | null {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getYearFromDate(value: string | Date | null | undefined): number {
    if (!value) return new Date().getFullYear();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? new Date().getFullYear() : date.getFullYear();
  }

  private loadJob(id: number): void {
    this.isLoading.set(true);
    this.jobsService.getJobById(id).subscribe({
      next: (data) => {
        this.job.set(data);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  protected onJobChanged(): void {
    const job = this.job();
    if (job) this.loadJob(job.id);
  }

  protected onEdit(): void {
    const job = this.job();
    if (!job) return;

    const dialogRef = this.dialog.open<EditJobComponent, EditJobDialogData, boolean>(
      EditJobComponent,
      {
        data: { jobId: job.id },
        width: '600px',
        disableClose: true
      }
    );

    dialogRef.afterClosed().subscribe(saved => {
      if (saved) this.loadJob(job.id);
    });
  }
}
